import { makeAutoObservable, runInAction } from 'mobx'
import { type SubscriptionStore } from 'entities/Subscription'
import { type Conversation } from 'entities/Conversation'

export class TrialSubscriptionStore {
  private _loading = false
  private _conversation: Conversation | null = null

  constructor(private _subscriptionStore: SubscriptionStore) {
    makeAutoObservable(this)
  }

  get entities() {
    return this._subscriptionStore.entities
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([
      this.entities.usersStore.initPromise,
      this.entities.organizationStore.initPromise,
      this.entities.featureFlagsStore.fetchFeatureFlags(),
      this._subscriptionStore.initPromise,
      this.entities.complianceStore.fetchSummary({ cache: true }),
      this.entities.complianceStore.fetchOrganizationBusinessProfile({ cache: true }),
      this.entities.complianceStore.fetchOrganizationTollFree({ cache: true }),
      this.entities.complianceStore.fetchOrganizationUKCompliance({ cache: true }),
      this.entities.inboxesStore.fetchInboxes({ cache: true }),
    ])

    runInAction(() => {
      this._loading = false
    })
  }

  setConversation = (item: Conversation | null) => {
    this._conversation = item
  }

  get loading() {
    return this._loading
  }

  get organizationTollFree() {
    return this.entities.complianceStore.organizationTollFree || null
  }

  get compliance10DLCServicesList() {
    return this.entities.complianceStore.complianceServicesList || null
  }

  get organizationBusinessProfile() {
    return this.entities.complianceStore.organizationBusinessProfile
  }

  get businessProfile() {
    return this.organizationBusinessProfile?.businessProfile
  }

  get summary() {
    return this.entities.complianceStore.summary
  }

  get isStatusCompleted() {
    return this.isComplianceNotRequired || this.isComplianceCompleted
  }

  get numbersTwilio() {
    const inboxes = this.entities.inboxesStore.sharedInboxes

    return inboxes.reduce<number[]>((state, current) => {
      if (current.numberIds.length) {
        const ids = current.numberIds.filter((id) => !this.entities.numbersStore.getItem(id)?.isPFT)

        state = [...ids, ...state]
      }

      return state
    }, [])
  }

  get isTollFreeNumber() {
    if (this.entities.subscriptionStore.isTrial) {
      return this.numbersTwilio.some((id) => this.entities.numbersStore.getItem(id)?.isTollFree)
    }

    const { currentInboxId, sharedInboxes, isUnifiedInbox } = this.entities.inboxesStore

    if (isUnifiedInbox) {
      if (this._conversation?.number_id) {
        const number = this.entities.numbersStore.getItem(this._conversation.number_id)
        return Boolean(number?.isTollFree)
      }
      return sharedInboxes.some((inbox) => inbox?.number?.isTollFree)
    }

    const inbox = sharedInboxes.find((inbox) => inbox.id === currentInboxId)
    return Boolean(inbox?.number?.isTollFree)
  }

  get isLocalNumber() {
    return !this.isTollFreeNumber
  }

  get isComplianceNotRequired() {
    return Boolean(this.isLocalNumber && this.summary?.target_country === 'CA')
  }

  get isComplianceCompleted() {
    if (this.isLocalNumber) {
      return (
        this.businessProfile?.isStatusApproved && this.compliance10DLCServicesList?.isStatusApproved
      )
    }

    return this.organizationTollFree?.isStatusVerified
  }

  get isQuestionnaire() {
    return this.entities.complianceStore.isQuestionnaire
  }

  get isAdminOrOwnerRole() {
    return this.entities.usersStore.user?.isAdminOrOwnerRole
  }

  get isNumberSelected() {
    return !Boolean(this.entities.usersStore.user?.isRequireNumber)
  }

  get conversationNumberType() {
    const item = this._conversation

    if (!item) return null

    const numberId = item.number_id
    const number = this.entities.numbersStore.getItem(numberId)

    if (number?.isPFT) return 'pft'
    if (number?.isTollFree) return 'toll_free'

    return 'local'
  }

  get stateTollFree() {
    if (this.organizationTollFree?.isStatusBlocked) {
      return 'step_2_reject'
    }

    if (this.organizationTollFree?.isStatusRejected) {
      return 'step_1_reject'
    }

    if (this.organizationTollFree?.isStatusVerified) {
      return 'step_1_upgrade'
    }

    if (
      this.organizationTollFree?.isStatusPendingReview ||
      this.organizationTollFree?.isStatusInCarrierReview
    ) {
      return 'step_2'
    }

    if (this.organizationTollFree?.isStatusInProgress) {
      return 'step_1'
    }

    return 'default'
  }

  get stateBusinessProfile10DLC() {
    if (this.businessProfile?.isStatusFailed || this.compliance10DLCServicesList?.isStatusFailed) {
      return 'step_1_reject'
    }

    if (
      this._subscriptionStore.isTrial &&
      ((this.businessProfile?.isStatusApproved &&
        this.compliance10DLCServicesList?.isStatusApproved) ||
        this.isComplianceNotRequired)
    ) {
      return 'step_1_upgrade'
    }

    if (
      this.businessProfile?.isStatusApproved &&
      (this.compliance10DLCServicesList?.isStatusPendingReview ||
        this.compliance10DLCServicesList?.isStatusInCarrierReview)
    ) {
      return 'step_4'
    }

    if (
      (this.businessProfile?.isStatusPendingReview ||
        this.businessProfile?.isStatusInCarrierReview) &&
      (this.compliance10DLCServicesList?.isStatusPendingReview ||
        this.compliance10DLCServicesList?.isStatusInCarrierReview)
    ) {
      return 'step_3'
    }

    if (
      (this.businessProfile?.isStatusPendingReview ||
        this.businessProfile?.isStatusInCarrierReview ||
        this.businessProfile?.isStatusApproved) &&
      (this.compliance10DLCServicesList?.isEmpty ||
        this.compliance10DLCServicesList?.isStatusInProgress)
    ) {
      return 'step_2'
    }

    if (this.businessProfile?.isStatusInProgress) {
      return 'step_1'
    }

    if (this.businessProfile?.isStatusEmpty) {
      return 'step_0'
    }

    return 'default'
  }

  get state() {
    if (this.isAdminOrOwnerRole) {
      if (this.isNumberSelected) {
        if (this.isLocalNumber) return 'number_local_owner'

        return 'number_tollfree_owner'
      }
      if (this.isQuestionnaire) return 'questionnaire_ready_owner'

      return 'questionnaire_not_ready_owner'
    }

    if (this.isNumberSelected) {
      if (this.isLocalNumber) return 'number_local_member'

      return 'number_tollfree_member'
    }

    if (this.isQuestionnaire) return 'questionnaire_ready_member'

    return 'questionnaire_not_ready_member'
  }

  get hide() {
    if (
      this.entities.organizationStore.isTwilioNumberVendor ||
      this.entities.usersStore.user?.isUKCountry
    )
      return true
    if (!this._subscriptionStore.isTrial) {
      if (this.isComplianceNotRequired) return true
      if (!this.isQuestionnaire || !this.isNumberSelected) return true

      if (this.isTollFreeNumber) {
        return Boolean(this.organizationTollFree?.isStatusVerified)
      }

      if (this.isLocalNumber) {
        return Boolean(
          this.businessProfile?.isStatusApproved &&
            this.compliance10DLCServicesList?.isStatusApproved
        )
      }

      return true
    }

    return !this.entities.organizationStore.trialEndsAtString
  }
}
